/* ------------------------------------------------------------------
 * src/pages/create-jobs.jsx   (Notification-Version)
 * ------------------------------------------------------------------ */
import * as React from 'react';
import config from '../config/config';
import { useNavigate } from 'react-router-dom';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from './../components/form/Input';
import { CustomDatePicker } from './../components/form/CustomDatePicker';
import { DropDownList } from './../components/form/DropDownList';
import { Switch } from '@progress/kendo-react-inputs';
import { Loader } from '@progress/kendo-react-indicators';
import CustomerAssignmentModal from './../components/CustomerAssignmentModal';

/* ───────────────────────────── Notification ────────────────────── */
import { AppContext } from '../AppContext';       //  ← neu!

const CreateJobs = () => {
  const navigate = useNavigate();
  const { showNotification } = React.useContext(AppContext);   //  ← neu!

  /* ----------------------------- State ---------------------------- */
  const [customers, setCustomers] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [jobActive, setJobActive] = React.useState(true);

  const [customerModalVisible, setCustomerModalVisible] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);

  const formRef = React.useRef(null);

  /* --------------------------- Daten laden ------------------------ */
  React.useEffect(() => {
    (async () => {
      try {
        const [custRes, userRes] = await Promise.all([
          fetch(`${config.host}/customers`),
          fetch(`${config.host}/users`)
        ]);

        const customersData = await custRes.json();
        const usersData = await userRes.json();

        setCustomers(
          customersData.map(c => ({
            value: c['Customer-ID'],
            label: c['Firma'],
            details: c
          }))
        );

        setUsers(
          usersData
            .map(u => ({
              value: u['BenutzerID'],
              label: `${u['Vorname']} ${u['Nachname']}`.trim()
            }))
            .sort((a, b) => a.label.localeCompare(b.label, 'de', { sensitivity: 'base' }))
        );
      } catch (err) {
        console.error('Fehler beim Laden:', err);
        showNotification('error', 'Fehler beim Laden der Daten.');
      } finally {
        setLoading(false);
      }
    })();
  }, [showNotification]);

  /* -------- Kunde aus Modal zurück → Formular befüllen ----------- */
  React.useEffect(() => {
    if (!selectedCustomer || !formRef.current) return;

    const c = customers.find(x => x.value === selectedCustomer.value)?.details;
    if (!c) return;

    const fp = formRef.current;
    fp.onChange('customer', { value: c['Customer-ID'] });
    fp.onChange('firma', { value: c.Firma });
    fp.onChange('ansprechpartner', {
      value: `${c.Titel || ''} ${c.Vorname || ''} ${c.Nachname || ''}`.trim()
    });
    fp.onChange('adresse', {
      value: `${c.Strasse || ''} ${c.Hausnummer || ''}, ${c.PLZ || ''} ${c.ORT || ''}`
    });
  }, [selectedCustomer, customers]);

  /* -------------------------- Job-ID ----------------------------- */
  const generateJobID = (customerId = '') => {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const rnd = Math.floor(1000 + Math.random() * 9000);
    return `${customerId}-JOB-${date}-${rnd}`;
  };

  /* ---------------------------- Submit --------------------------- */
  const onSubmit = async (dataItem) => {
    try {
      setLoading(true);

      const customerId = selectedCustomer?.value || '';
      const jobID = generateJobID(customerId);

      const assignedEmployees = dataItem.employee
        ? [dataItem.employee.value ?? dataItem.employee]
        : [];

      const jobData = {
        JobID: jobID,
        CustomerID: customerId,
        AssignedEmployee: assignedEmployees,
        JobIDcustom: dataItem.auftragsnummer || '',
        Kurzbeschreibung: dataItem.kurzbeschreibung || '',
        AusfuehrungszeitStart:
          dataItem.AusfuehrungszeitStart instanceof Date && !isNaN(dataItem.AusfuehrungszeitStart)
            ? dataItem.AusfuehrungszeitStart.toISOString()
            : null,
        AusfuehrungszeitEnde:
          dataItem.AusfuehrungszeitEnde instanceof Date && !isNaN(dataItem.AusfuehrungszeitEnde)
            ? dataItem.AusfuehrungszeitEnde.toISOString()
            : null,
        EinsatzStrasse: dataItem.strasse || '',
        EinsatzHausnummer: dataItem.hausnummer || '',
        EinsatzPLZ: dataItem.plz || '',
        EinsatzORT: dataItem.ort || '',
        JobActive: jobActive
      };

      const res = await fetch(`${config.host}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobData)
      });

      if (!res.ok) {
        const errText = await res.text();
        showNotification('error', `Fehler: ${errText}`);
        return;
      }

      showNotification('success', 'Auftrag erfolgreich erstellt!');
      navigate('/jobs');
    } catch (err) {
      console.error(err);
      showNotification('error', 'Fehler beim Erstellen des Auftrags.');
    } finally {
      setLoading(false);
    }
  };

  const onCancelClick = () => navigate('/jobs');

  /* --------------------------- Render ---------------------------- */
  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Loader size="large" type="infinite-spinner" />
        <p>Daten werden geladen…</p>
      </div>
    );
  }

  return (
    <div id="CreateJobs" className="profile-page main-content">
      <div className="card-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="card-title">Auftragsverwaltung</h3>
        </div>

        <div className="card-component">
          <Form
            initialValues={{
              customer: '', firma: '', ansprechpartner: '', adresse: '',
              AusfuehrungszeitStart: null, AusfuehrungszeitEnde: null,
              auftragsnummer: '', kurzbeschreibung: '',
              strasse: '', hausnummer: '', plz: '', ort: '', employee: ''
            }}
            onSubmit={onSubmit}
            render={(fp) => {
              formRef.current = fp;

              const setDate = (field) => (e) => {
                let d = e?.value ?? null;
                if (typeof d === 'string' && d.trim()) d = new Date(d);
                if (!(d instanceof Date) || isNaN(d)) d = null;

                fp.onChange(field, { value: d });

                if (field === 'AusfuehrungszeitStart' && d) {
                  const endDate = new Date(d.getTime() + 8 * 60 * 60 * 1000);
                  fp.onChange('AusfuehrungszeitEnde', { value: endDate });
                }
              };

              return (
                <>
                  <FormElement horizontal style={{ maxWidth: 700 }}>
                    {/* ---------------- Kundendaten ---------------- */}
                    <h3>Kundendaten</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <label style={{ width: 115, marginLeft: 135 }}>Kunde</label>
                      <Button type="button" onClick={() => setCustomerModalVisible(true)} style={{ width: 115, marginLeft: -75 }}>
                        Kundenauswahl
                      </Button>
                      {selectedCustomer && <span><strong>{selectedCustomer.label}</strong></span>}
                    </div>

                    <Field name="firma" label="Firma" component={Input} disabled />
                    <Field name="ansprechpartner" label="Ansprechpartner" component={Input} disabled />
                    <Field name="adresse" label="Adresse" component={Input} disabled />

                    {/* ---------------- Auftragsdaten ---------------- */}
                    <hr /><h3>Auftragsdaten</h3>
                    <Field name="AusfuehrungszeitStart" label="Startdatum und Uhrzeit"
                      component={CustomDatePicker} format="dd.MM.yyyy HH:mm" timeFormat="HH:mm"
                      onChange={setDate('AusfuehrungszeitStart')} />
                    <Field name="AusfuehrungszeitEnde" label="Enddatum und Uhrzeit"
                      component={CustomDatePicker} format="dd.MM.yyyy HH:mm" timeFormat="HH:mm"
                      onChange={setDate('AusfuehrungszeitEnde')} />
                    <Field name="auftragsnummer" label="Auftragsnummer" component={Input} />
                    <Field name="kurzbeschreibung" label="Kurzbeschreibung" component={Input} />

                    {/* ---------------- Einsatzort ---------------- */}
                    <hr /><h3>Einsatzort</h3>
                    <Field name="strasse" label="Straße" component={Input} />
                    <Field name="hausnummer" label="Hausnummer" component={Input} />
                    <Field name="plz" label="PLZ" component={Input} />
                    <Field name="ort" label="Ort" component={Input} />

                    {/* ---------------- Mitarbeiter ---------------- */}
                    <hr /><h3>Mitarbeiter</h3>
                    <Field name="employee" label="Mitarbeiter"
                      component={DropDownList}
                      data={users} textField="label" dataItemKey="value" />

                    {/* ---------------- Optionen ---------------- */}
                    <hr /><h3>Optionen</h3>
                    <div style={{ marginLeft: 115, marginTop: 20 }}>
                      <label style={{ marginRight: 10 }}>Job aktiv:</label>
                      <Switch checked={jobActive} onChange={e => setJobActive(e.value)} />
                    </div>

                    {/* ---------------- Buttons ---------------- */}
                    <hr />
                    <div className="k-form-buttons">
                      <Button onClick={onCancelClick}>Abbrechen</Button>
                      <Button themeColor="primary" type="submit" disabled={!fp.allowSubmit}>
                        Speichern
                      </Button>
                    </div>
                  </FormElement>

                  {/* --------------- Kunden-Auswahl-Modal --------------- */}
                  <CustomerAssignmentModal
                    visible={customerModalVisible}
                    initialCustomer={selectedCustomer?.value}
                    customers={customers}
                    onClose={(cust) => {
                      if (cust) setSelectedCustomer(cust);
                      setCustomerModalVisible(false);
                    }}
                  />
                </>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateJobs;
